import cx from 'clsx';
import React from 'react';
import { type Control, useController } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as InfoIcon } from '@assets/svg/ic-info.svg';

import Input from '@components/uikit/Input/Input';

import { toCurrency } from '@helpers/helper';

import useTranslation from '@hooks/useTranslation';

import type { IAiCostSavingSettings } from '@model/aiCostSavingSettings';
import type { ICurrency } from '@model/currency';

import { useCustomerInfoQuery } from '@services/useCustomerInfoQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import { COMPLETION_REPORT_FIELDS } from '../constants/schemaCompletionReport';
import MetricsFieldType from './MetricsFieldType/MetricsFieldType';
import style from './style.module.scss';

interface IProps {
  control: Control;
  costAvoided: number;
  costSavingSettings: IAiCostSavingSettings;
  costSavingTooltips: { [key: string]: string };
  currency: ICurrency;
}

const StepMetricsInfo: React.FC<IProps> = ({
  control,
  costAvoided,
  costSavingSettings,
  costSavingTooltips,
  currency,
}) => {
  const { t } = useTranslation();
  const { customerId } = useParams();
  const locationSelectValue = selectAppLocationSelectValue();

  const { data: customerInfoData } = useCustomerInfoQuery({
    customerId,
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
  });

  const hasLphAvoided = !!costSavingSettings?.use_lph_avoided;
  const multiplicationFactor =
    costSavingSettings.multiplication_factor_in_currency ?? costSavingSettings.multiplication_factor;
  const { savings, downtime } = costSavingTooltips;

  const {
    field: { value: downtimeTypeValue, onChange: downtimeTypeChange },
  } = useController({
    control,
    name: COMPLETION_REPORT_FIELDS.DOWNTIME_TYPE,
  });

  return (
    <div className={cx(style.stepMetrics, style.scrollingBlock)}>
      <div
        className={cx(style.metricsInfoForm, style.twoParts, {
          [style.threeParts]: hasLphAvoided,
        })}
      >
        <div className={style.relative}>
          <Input
            inputType={'number'}
            name={COMPLETION_REPORT_FIELDS.DOWNTIME_AVOIDED}
            control={control}
            label={t('Unplanned downtime avoided')}
            rightElement={
              <MetricsFieldType
                downtimeType={downtimeTypeValue}
                onClick={() => downtimeTypeChange(+!downtimeTypeValue)}
              />
            }
            required
          />
          {downtime && (
            <InfoIcon
              data-tooltip-id="metrics-info"
              data-tooltip-content={downtime}
              className={style.infoIcon}
            />
          )}
        </div>
        {hasLphAvoided && (
          <Input
            inputType={'number'}
            name={COMPLETION_REPORT_FIELDS.LPH_AVOIDED}
            control={control}
            label={t('Lph Avoided')}
            rightElement={
              <MetricsFieldType
                downtimeType={downtimeTypeValue}
                onClick={() => downtimeTypeChange(+!downtimeTypeValue)}
              />
            }
            required
          />
        )}
        <div className={style.relative}>
          <Input
            inputType={'number'}
            name={COMPLETION_REPORT_FIELDS.EQUIPMENT_LABOR_SAVINGS}
            control={control}
            label={t('Equipment/Labor savings')}
            leftElement={<span className={style.fieldMark}>{currency.symbol}</span>}
          />
          {savings && (
            <InfoIcon
              data-tooltip-id="metrics-info"
              data-tooltip-content={savings}
              className={style.infoIcon}
            />
          )}
        </div>
      </div>
      {customerInfoData?.showTotalCostSavings && (
        <>
          <div className={style.totalCsBlock}>
            <div className={style.fieldWrapper}>
              <label className={cx(style.fieldLabel, style.disabledLabel)}>
                {t('Multiplication factor')}
              </label>
              <div className={style.fieldValueDisabled} data-qa={'multiplication_factor'}>
                <span className={style.fieldMark}>{currency.symbol}</span>
                {toCurrency(multiplicationFactor)}
              </div>
            </div>
            <div className={style.fieldWrapper}>
              <label className={cx(style.fieldLabel, style.disabledLabel)}>{t('Cost avoided')}</label>
              <div className={style.fieldValueDisabled} data-qa={'cost_avoided'}>
                <span className={style.fieldMark}>{currency.symbol}</span>
                {toCurrency(costAvoided)}
              </div>
            </div>
          </div>
          {(savings || downtime) && <Tooltip id="metrics-info" className={style.metricsInfoTooltip} />}
        </>
      )}
    </div>
  );
};

export default StepMetricsInfo;
